import Grid, { type GridItem } from "@client/controls/Grid/component";
import { isShallowEqual } from "@client/lib/object";
import * as React from "react";
import { fetchFeed, type InstagramFeedOptions, type InstagramItem } from "./lib";

export interface InstagramFeedProps {
  columns: number;
  columnWidth: string;
  feedOptions: InstagramFeedOptions;
  itemStyle: React.CSSProperties;
  itemGap: string;
}

interface State {
  isLoading: boolean;
  error: string | null;
  items: InstagramItem[];
}

const InstagramItem = React.memo<InstagramItem & GridItem>(
  (props: InstagramItem & GridItem & { itemStyle: React.CSSProperties }) => (
    <div className="photo" style={props.itemStyle}>
      <a href={props.link} target="_blank" title="Instagram Photo related with instagram">
        <img
          alt="Instagram Photo related with instagram"
          src={props.images.thumbnail.url}
          height={props.images.thumbnail.height}
          width={props.images.thumbnail.width}
        />
      </a>
    </div>
  )
);

class InstagramFeed extends React.PureComponent<InstagramFeedProps, State> {
  public state: State = {
    isLoading: false,
    error: null,
    items: []
  };

  public componentDidMount() {
    this._fetch(this.props);
  }

  public componentDidUpdate(prevProps: Readonly<InstagramFeedProps>, prevState: Readonly<State>, snapshot?: any): void {
    if (!isShallowEqual(this.props.feedOptions, prevProps.feedOptions)) {
      this._fetch(this.props);
    }
  }

  public render() {
    return (
      <>
        {!this.props.feedOptions.accessToken ? (
          <p>
            Due to updates in Instagram policy, you should add instagram token.{" "}
            <a href="https://elfsight.com/blog/2016/05/how-to-get-instagram-access-token/" target="_blank">
              Check here how to get your token.
            </a>
          </p>
        ) : this.state.error ? (
          <p className="error">{this.state.error}</p>
        ) : (
          <Grid
            columnWidth={this.props.columnWidth}
            columns={this.props.columns}
            itemGap={this.props.itemGap}
            items={this.state.items}
            ItemComponent={InstagramItem}
            itemProps={{ itemStyle: this.props.itemStyle }}
          />
        )}
      </>
    );
  }

  private _fetch = async (props: InstagramFeedProps) => {
    if (this.state.isLoading) return;
    if (!props.feedOptions.accessToken) return;

    if (props.feedOptions.what === "tags" && !props.feedOptions.search) {
      this.setState({ error: "Tag name is required" });
      return;
    }

    this.setState({ isLoading: true });
    try {
      const feed = await fetchFeed(props.feedOptions);
      if (feed.meta.error_message) {
        this.setState({ error: feed.meta.error_message! });
      } else {
        this.setState({ error: null, items: feed.data! });
      }
    } finally {
      this.setState({ isLoading: false });
    }
  };
}

export default InstagramFeed;
